import * as React from 'react';
import { Document, Font, Image, Page, Text, View } from '@react-pdf/renderer';
import PDFHeader from '../../../components/PDF/PDFLayout/PDFHeader';
import PDFFooter from '../../../components/PDF/PDFLayout/PDFFooter';
import { IProjectInfoForm } from '../../../pages/ProjectInfo/IProjectInfo';
import { IProjectMacra2 } from '../../../MACRA_2/store/project/projectSlice';
import { PdfCrossCheck, styles } from '../../../components/PDF/PDFStyle';
import { ISteppedGabionResults } from '../../../MACRA_2/api/IApiResponse';
import ProjectInformation from '../PDFComponents/ProjectInformation';
import PDFTable from '../PDFTable/PDFTable';
import { useTranslation } from 'react-i18next';
import { conversionLabel } from '../../../conversion';
import { IWingsGeometrySteppedWeirForm } from '../../../MACRA_2/components/InputForm/WingsGeometry/IWingsGeometry';
import { IStillingBasinSteppedWeirSlopedWeirForm } from '../../../MACRA_2/components/InputForm/StillingBasin/IStillingBasin';
import { IWeirGeometrySteppedWeirForm } from '../../../MACRA_2/components/InputForm/WeirGeometry/IWeirGeometry';
import { roundToTwo } from '../../../MACRA_1/components/InputForm/Geometry/utils';
import { IHydraulicDataSteppedWeirForm } from '../../../MACRA_2/components/InputForm/HydraulicData/IHydraulicData';
import { EDissipation } from '../../../MACRA_2/enum/general';

interface IPDFSteppedProps {
    project: IProjectMacra2;
    projectInfo: IProjectInfoForm;
    results: ISteppedGabionResults;
}

const PDFStepped = (props: IPDFSteppedProps) => {
    const { t } = useTranslation();
    const { project, projectInfo, results } = props;

    const input = [
        [
            t('output_hydraulic_stepped_crest_width'),
            '@L(g)',
            `${roundToTwo((project.wingsGeometry as IWingsGeometrySteppedWeirForm).width)}`,
            `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`,
        ],
        [
            t('output_hydraulic_stepped_toe_width'),
            '@L(t)',
            `${roundToTwo((project.stillingBasin as IStillingBasinSteppedWeirSlopedWeirForm).width)}`,
            `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`,
        ],
        [
            t('output_hydraulic_stepped_step_height'),
            'h',
            `${roundToTwo((project.weirGeometry as IWeirGeometrySteppedWeirForm).stepHeight)}`,
            `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`,
        ],
        [
            t('output_hydraulic_stepped_total_height'),
            '@f(g)',
            `${roundToTwo(
                ((project.weirGeometry as IWeirGeometrySteppedWeirForm).stepNumber! - (project.weirGeometry as IWeirGeometrySteppedWeirForm).foundationLayers!) *
                    (project.weirGeometry as IWeirGeometrySteppedWeirForm).stepHeight,
            )}`,
            `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`,
        ],
        [t('output_hydraulic_stepped_sloped_discharge'), 'Q', `${roundToTwo(project.hydraulicData?.discharge)}`, `${conversionLabel.m3s_ft3s[projectInfo.unitOfMeasure as number]}`],
        [
            t('output_hydraulic_stepped_specific_discharge_crest'),
            '@q(c)',
            `${roundToTwo(project.hydraulicData?.discharge! / (project.wingsGeometry as IWingsGeometrySteppedWeirForm).width)}`,
            `${conversionLabel.m3sm_ft3sft[projectInfo.unitOfMeasure as number]}`,
        ],
        [
            t('output_hydraulic_stepped_specific_discharge_toe'),
            '@q(t)',
            `${roundToTwo(project.hydraulicData?.discharge! / roundToTwo((project.stillingBasin as IStillingBasinSteppedWeirSlopedWeirForm).width))}`,
            `${conversionLabel.m3sm_ft3sft[projectInfo.unitOfMeasure as number]}`,
        ],
        [t('output_hydraulic_stepped_discharge_coefficient'), 'µ', '0.385', '-'],
        [t('output_hydraulic_stepped_average_slope'), 'V:H', `${roundToTwo((project.weirGeometry as IWeirGeometrySteppedWeirForm).stepRatio)}`, '-'],
        [
            (project.hydraulicData as IHydraulicDataSteppedWeirForm).dissipationType === EDissipation.Manual ? t('output_hydraulic_stepped_dissipation') : t('input_automated_dissipation'),
            'α',
            `${roundToTwo(results.basinDesign.dissipation)}`,
            '-',
        ],
        [t('output_hydraulic_stepped_sloped_roughnessCoefficient'), 'n', `${roundToTwo(project.hydraulicData?.roughnessCoefficient)}`, '-'],
        [t('output_hydraulic_stepped_river_bed_gradient'), 'i', `${roundToTwo(project.hydraulicData?.riverBedGradient)}`, '-'],
    ];
    const upstream = [
        [t('output_hydraulic_stepped_backwater_depth'), '@d(0)', `${roundToTwo(results.hydraulicUpstream.backwaterDepth)}`, `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`],
        [t('output_hydraulic_stepped_critical_depth'), '@d(c)', `${roundToTwo(results.hydraulicUpstream.criticalDepth)}`, `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`],
        [t('output_hydraulic_stepped_critical_velocity'), '@V(k)', `${roundToTwo(results.hydraulicUpstream.criticalVelocity)}`, `${conversionLabel.ms_fts[projectInfo.unitOfMeasure as number]}`],
        [t('output_hydraulic_stepped_specific_head_crest'), '@H(s)', `${roundToTwo(results.hydraulicUpstream.specificHeadOnTheCrest)}`, `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`],
        [t('output_hydraulic_stepped_total_head_crest'), '@H(t)', `${roundToTwo(results.hydraulicUpstream.totalHeadOnTheCrest)}`, `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`],
        [t('output_hydraulic_stepped_type_flow'), '-', results.hydraulicUpstream.typeOfFlow!, '-'],
    ];
    const downstream = [
        [
            t('output_hydraulic_stepped_total_head_downstream'),
            '@H(t)',
            `${roundToTwo(results.hydraulicDownstream.totalHeadDownstream)}`,
            `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`,
        ],
        [t('output_hydraulic_stepped_critical_depth'), '@y(cr)', `${roundToTwo(results.hydraulicDownstream.criticalDepth)}`, `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`],
        [t('output_hydraulic_stepped_critical_velocity'), '@V(k)', `${roundToTwo(results.hydraulicDownstream.criticalVelocity)}`, `${conversionLabel.ms_fts[projectInfo.unitOfMeasure as number]}`],
        [t('output_hydraulic_stepped_specific_head_toe'), '@H(s)', `${roundToTwo(results.hydraulicDownstream.specificHeadAtTheToe)}`, `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`],
        [
            t('output_hydraulic_stepped_water_depth_supercritical_flow'),
            '@z(1)',
            `${roundToTwo(results.hydraulicDownstream.waterDepth)}`,
            `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`,
        ],
        [t('output_hydraulic_stepped_froude_number'), '@F(r)', `${roundToTwo(results.hydraulicDownstream.froudeNumber)}`, '-'],
        [t('output_hydraulic_stepped_conjugate_depth'), '@z(2)', `${roundToTwo(results.hydraulicDownstream.conjugateDepth)}`, `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`],
        [
            t('output_hydraulic_stepped_hydraulic_radius_superctitical_flow'),
            '@R(h)',
            `${roundToTwo(results.hydraulicDownstream.hydraulicRadius)}`,
            `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`,
        ],
        [t('output_hydraulic_stepped_average_rate_friction'), 'if', `${roundToTwo(results.hydraulicDownstream.averageRateOfFriction)}`, '-'],
        [
            t('output_hydraulic_stepped_hydraulic_jump_length'),
            '@L(j)',
            `${roundToTwo(results.hydraulicDownstream.hydraulicJumpLength)}`,
            `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`,
        ],
        [t('output_hydraulic_stepped_downstream_water_depth'), '@z(3)', `${roundToTwo(results.hydraulicDownstream.z3)}`, `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`],
        [t('output_hydraulic_stepped_downstream_velocity'), '@v(3)', `${roundToTwo(results.hydraulicDownstream.v3)}`, `${conversionLabel.ms_fts[projectInfo.unitOfMeasure as number]}`],
    ];

    const staticSteppedBasinDesign = [
        [t('output_static_stepped_min_length_crest'), '@L(crest)', `${roundToTwo(results.basinDesign.minLengthOfTheCrest)}`, `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`, '-'],
        [
            t('output_static_stepped_min_length_stilling_basin'),
            '@L(bac)',
            `${roundToTwo(results.basinDesign.minLengthOfTheStillingPool)}`,
            `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`,

            project.stillingBasin?.poolLength! >= results.basinDesign.minLengthOfTheStillingPool! ? PdfCrossCheck.check : PdfCrossCheck.cross,
        ],
        [t('output_static_stepped_stilling_basin_width'), '@W(b)', `${roundToTwo(project.stillingBasin?.width)}`, `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`, '-'],
        [
            t('output_static_stepped_min_height'),
            'fc_min',
            `${roundToTwo(results.basinDesign.minCounterweirHeight)}`,
            `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`,

            project.stillingBasin?.counterweirElevation! >= results.basinDesign.minCounterweirHeight! ? PdfCrossCheck.check : PdfCrossCheck.cross,
        ],
        [t('output_static_stepped_water_velocity'), '@v(1)', `${roundToTwo(results.basinDesign.waterVelocity)}`, `${conversionLabel.ms_fts[projectInfo.unitOfMeasure as number]}`, '-'],
        [
            t('output_hydraulic_vertical_stepped_sloped_lining_material'),
            `${project.stillingBasin?.material.name!}`,
            `t=${roundToTwo(project.stillingBasin?.material.physicalData![0].thickness)} ${conversionLabel.m_in[projectInfo.unitOfMeasure as number]}`,
            `d50=${roundToTwo(project.stillingBasin?.material.physicalData![0].rockD50)} ${conversionLabel.m_in[projectInfo.unitOfMeasure as number]}`,
            `Cu=${roundToTwo(project.stillingBasin?.material.physicalData![0].cu)}`,
        ],
        [
            t('output_hydraulic_vertical_stepped_sloped_shear_stress_downstream_section'),
            'τ',
            `${roundToTwo(results.basinDesign.shearStress)}`,
            `${conversionLabel.Nm2_lbfft2[projectInfo.unitOfMeasure as number]}`,

            project.stillingBasin?.material.physicalData![0].allowShearStress! >= results.basinDesign.shearStress! ? PdfCrossCheck.check : PdfCrossCheck.cross,
        ],
        [
            t('output_hydraulic_vertical_stepped_sloped_allowable_shear_stress_lining'),
            '@τ(all)',
            `${project.stillingBasin?.material.physicalData![0].allowShearStress}`,
            `${conversionLabel.Nm2_lbfft2[projectInfo.unitOfMeasure as number]}`,
            '-',
        ],
    ];

    return (
        <View style={styles.gap3}>
            <View style={styles.rowSpaced}>
                <Text style={styles.boldGreen}>{t('graphical_results_hydraulic')}</Text>
            </View>
            <PDFTable columns={[t('output_table_title_input'), t('output_symbol'), t('output_value'), t('output_unit')]} data={input} />
            <PDFTable columns={[t('output_hydraulic_sloped_upstream_parameters'), t('output_symbol'), t('output_value'), t('output_unit')]} data={upstream} />
            <PDFTable columns={[t('output_hydraulic_sloped_downstream_parameters'), t('output_symbol'), t('output_value'), t('output_unit')]} data={downstream} />
            <View break>
                <View style={styles.rowSpaced}>
                    <Text style={styles.boldGreen}>{t('graphical_results_basinDesign')}</Text>
                </View>
                <PDFTable columns={[t('output_static_vertical_stepped_basin_design'), t('output_symbol'), t('output_value'), t('output_unit'), t('output_check')]} data={staticSteppedBasinDesign} />
            </View>
        </View>
    );
};

export default PDFStepped;
